import React from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Card,
  CardBody,
  CardHeader,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  Text,
  VStack,
  useColorModeValue
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiHelpCircle, FiLogIn } from 'react-icons/fi';
import ColorModeToggle from '../components/color-mode-toggle';
import { LOGIN_PAGE_ROUTE, LINK_COLOR } from '../constants';

interface FaqItem {
  question: string;
  answer: string;
}

const FAQ_ITEMS: FaqItem[] = [
  {
    question: '¿Qué puedo hacer en la sede virtual?',
    answer:
      'Podés consultar el estado de tu cuenta de socio: visualizar y modificar tus datos personales y credencial (carnet) de socio y, además, ver el estado de cuotas sociales, actividades y seguro, y realizar los pagos correspondientes de manera rápida y segura.'
  },
  {
    question: '¿Necesito crear un usuario para poder acceder a la sede virtual?',
    answer:
      'Sí. Necesitás crear un usuario para poder acceder a la sede virtual pero es algo que se hace de manera muy rápida y sencilla, ¡y por única vez! Solo necesitás brindarnos un correo electrónico válido y definir una contraseña segura. Luego, te enviaremos un correo de verificación para confirmar tu cuenta: sólo tenés que hacer click en un enlace, ¡y listo! Una vez creado tu usuario, podés iniciar sesión con tu correo y contraseña.'
  },
  {
    question: '¿Qué hago si no me llega el correo de verificación?',
    answer:
      'Revisá primero la carpeta de spam o correo no deseado. Si no aparece ahí, esperá unos minutos y volvé a solicitar el envío desde la aplicación.'
  },
  {
    question: '¿Qué debo hacer durante mi primer ingreso a la sede virtual?',
    answer:
      'En tu primer ingreso a la sede virtual, tenés que vincular tu cuenta de usuario con tu número de socio, así podemos saber quién sos. Para ello, verás una pantalla en la que podrás buscar por número de socio o documento de identidad. Una vez encontrado, podés vincular tu cuenta y ¡listo, ya podés usar la sede virtual! Si no podés encontrar tu número de socio, podés contactar a la secretaría para que te ayuden.'
  },
  {
    question: '¿Puedo vincular mi cuenta de usuario con más de un socio?',
    answer:
      'Sí. Es el caso típico de los padres y madres del club. En este caso, luego de crear tu cuenta, en el primer inicio de sesión, vinculala a uno de los socios que desees. Luego, cuando ingreses a la pantalla principal de la sede virtual, hacé click en la flecha hacia abajo que está a la derecha del nombre y número de socio vinculado previamente, en la parte superior derecha de la pantalla. Ahí se abrirá un menú y podrás ver la opción "Vincular otro socio". Hacé click en esa opción y seguí el mismo proceso que hiciste para vincular tu cuenta con el primer socio. Al finalizar, en la parte superior derecha de la pantalla, haciendo click en la flecha hacia abajo, podrás cambiar el socio con el que estás navegando en la sede virtual.'
  },
  {
    question: '¿Cómo recupero mi contraseña si no puedo iniciar sesión?',
    answer:
      'Podés usar la opción "¿Olvidaste tu contraseña?" en la pantalla de inicio de sesión. Te vamos a enviar un correo con un enlace para restablecerla.'
  },
  {
    question: '¿Puedo actualizar mis datos desde la sede virtual?',
    answer:
      'Sí. Aunque sólo algunos de ellos. Una vez dentro de tu cuenta, podés ingresar a la pestaña "Mis datos" y actualizar tus datos de contacto. Los datos personales y datos de socio no pueden ser modificados desde la sede virtual. Por ese motivo, si detectás un error en un dato no editable, por favor comunicate con nosotros así lo podemos modificar por vos.'
  },
  {
    question: '¿Dónde veo el estado de mi cuotas sociales o pagos pendientes?',
    answer:
      'En la página de inicio hay una sección llamada "Cuotas sociales" que te mostrará el estado actual de tus cuotas sociales. Dentro de ella, también se encuentra el botón "Ver mis cuotas sociales" que te llevará a la página de donde podrás ver el detalle de tus cuotas sociales y realizar los pagos correspondientes. Otra posibilidad es hacer click en la pestaña "Mis cuotas" en la barra lateral izquierda.'
  },
  {
    question: '¿Dónde veo el estado de mis actividades y mi seguro o pagos pendientes?',
    answer:
      'En la barra lateral izquierda podrás encontrar las pestañas "Mis actividades" y "Mi seguro". Al hacer click en cada una de ellas, podrás ver el estado actual de tus actividades y seguro, y realizar los pagos correspondientes.'
  },
  {
    question: '¿Puedo pagar todo junto: cuota social y actividad?',
    answer:
      'Sí. Lo podés hacer desde la sección "Mis cuotas sociales", accediendo mediante la pestaña "Mis cuotas" en la barra lateral izquierda. Una vez ahí, podés seleccionar la cuota social que querés abonar. Posteriormente, si estás dado de alta en una actividad, el sistema te permitirá elegir si querés abonar todo junto o únicamente la cuota social.'
  },
  {
    question: '¿Qué medios de pago puedo utilizar para pagar mis cuotas sociales, actividades o seguro?',
    answer:
      'Los medios de pago disponibles son: tarjetas de crédito/débito, MercadoPago y transferencia bancaria. Podés elegir el medio de pago que prefieras.'
  },
  {
    question: 'Al realizar un pago, ¿cuándo se reflejará en la sede virtual?',
    answer:
      'Los pagos realizados con tarjetas de crédito/débito y MercadoPago se procesan inmediatamente, por lo que verás la cuota social y/o actividad marcada como "Abonada" en la sede virtual luego de haber confirmado la operación. Por otro lado, los pagos efectuados mediante transferencia bancaria tendrán demora en verse reflajdos en la sede virtual, ya que necesitan ser verificados manualmente por la secretaría. Mientras tanto, el pago quedará marcado como "En verificación". Si al cabo de 48 horas hábiles no se ha realizado la verificación, podés contactar a la secretaría para que te ayuden a resolver el problema.'
  },
  {
    question: '¿Cómo me contacto con secretaría si tengo un problema?',
    answer:
      'Si tenés un inconveniente que no podés resolver desde la sede virtual, podés contactarte con secretaría por los canales habituales del club para recibir asistencia.'
  }
];

function Help(): JSX.Element {
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('green.700', 'green.300');
  const borderColor = useColorModeValue('green.100', 'gray.700');
  const accordionButtonHoverBg = useColorModeValue('green.50', 'whiteAlpha.100');
  const bgGradientOverlay = useColorModeValue(
    'linear(135deg, rgba(247, 250, 252, 0.85) 0%, rgba(240, 253, 244, 0.85) 100%)',
    'linear(135deg, rgba(26, 32, 44, 0.85) 0%, rgba(45, 55, 72, 0.85) 100%)'
  );

  return (
    <Box
      minH="100vh"
      position="relative"
      py={8}
      className="fade-in-up"
      backgroundImage="url('/pacifico-sede.gif')"
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      backgroundAttachment={{ base: 'scroll', md: 'fixed' }}
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgGradient: bgGradientOverlay,
        zIndex: 1
      }}
    >
      <Container
        maxW={{ base: 'lg', md: '3xl', lg: '5xl' }}
        centerContent
        position="relative"
        zIndex={2}
        width="100%"
      >
        <Flex direction="column" align="center" w="full" mb={8}>
          <HStack justify="space-between" w="full" mb={4}>
            <Box />
            <ColorModeToggle />
          </HStack>

          <Card
            w="full"
            maxW={{ base: 'md', md: '2xl', lg: '4xl' }}
            shadow="2xl"
            bg={cardBg}
            borderRadius="2xl"
            border="1px"
            borderColor={borderColor}
            className="card-hover"
            overflow="hidden"
          >
            <CardHeader
              bg={useColorModeValue('green.500', 'green.600')}
              color="white"
              textAlign="center"
              py={6}
            >
              <VStack spacing={2}>
                <Icon as={FiHelpCircle} boxSize={8} />
                <Heading size="lg" fontWeight="bold">
                  Preguntas frecuentes
                </Heading>
                <Text fontSize="sm" opacity={0.9}>
                  Resolvemos las dudas más comunes
                </Text>
              </VStack>
            </CardHeader>

            <CardBody p={6}>
              <VStack spacing={5} align="stretch">
                <Text color={headingColor} fontWeight="semibold" textAlign="center">
                  Encontrá ayuda rápida sobre el uso de la sede virtual
                </Text>

                <Accordion allowToggle>
                  {FAQ_ITEMS.map((item) => (
                    <AccordionItem key={item.question} borderColor={borderColor}>
                      <h2>
                        <AccordionButton
                          py={4}
                          _hover={{ bg: accordionButtonHoverBg }}
                          _expanded={{ bg: accordionButtonHoverBg, color: headingColor }}
                        >
                          <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                            {item.question}
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4} color={textColor}>
                        <Text textAlign="justify">{item.answer}</Text>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </VStack>
            </CardBody>
          </Card>
        </Flex>
      </Container>
    </Box>
  );
}

export default Help;
